import axios from '../utils/setupAxiosAuth';
import API_ROUTES from '../routes/routes';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  AudienceCustomer,
  AudienceFilters,
  BroadcastJob,
  BroadcastProvider,
  BroadcastRecipient,
  WhatsAppTemplate,
} from './types';

const base = () => `${getApiBaseUrl()}${API_ROUTES.whatsappBroadcasts}`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('user_authToken')}`,
});

export const fetchTemplates = async (provider: BroadcastProvider, sync = false) => {
  const response = await axios.get(`${base()}/templates`, {
    headers: authHeaders(),
    params: { provider, sync },
  });
  return response.data.templates as WhatsAppTemplate[];
};

export const fetchTemplate = async (id: number) => {
  const response = await axios.get(`${base()}/templates/${id}`, {
    headers: authHeaders(),
  });
  return response.data as { template: WhatsAppTemplate; preview: string };
};

export const fetchAudienceIds = async (filters: AudienceFilters) => {
  const response = await axios.post(
    `${base()}/audience/ids`,
    { filters },
    { headers: authHeaders() }
  );
  return response.data as { customer_ids: number[]; total: number };
};

export const previewAudience = async (filters: AudienceFilters) => {
  const response = await axios.post(
    `${base()}/audience/preview`,
    { filters },
    { headers: authHeaders() }
  );
  return response.data as { count: number; sample: AudienceCustomer[] };
};

export const searchAudience = async (
  filters: AudienceFilters,
  page = 0,
  perPage = 25
) => {
  const response = await axios.post(
    `${base()}/audience/search`,
    { filters },
    {
      headers: authHeaders(),
      params: { _start: page * perPage, _end: (page + 1) * perPage },
    }
  );
  return {
    rows: response.data.rows as AudienceCustomer[],
    total: parseInt(response.headers['x-total-count'] || String(response.data.total || 0), 10),
  };
};

export const fetchStates = async () => {
  const response = await axios.get(`${base()}/audience/states`, {
    headers: authHeaders(),
  });
  return response.data.states as string[];
};

export const createBroadcastJob = async (payload: {
  provider: BroadcastProvider;
  template_id: number;
  audience_filter: AudienceFilters;
  max_recipients?: number | null;
  dry_run?: boolean;
  allow_large_audience?: boolean;
}) => {
  const response = await axios.post(`${base()}/jobs`, payload, {
    headers: authHeaders(),
  });
  return response.data as { job: BroadcastJob; launch: { mode: string; task_arn: string } };
};

export const fetchJobs = async (page = 0, perPage = 25, status?: string) => {
  const response = await axios.get(`${base()}/jobs`, {
    headers: authHeaders(),
    params: {
      _start: page * perPage,
      _end: (page + 1) * perPage,
      status: status || undefined,
    },
  });
  return {
    jobs: response.data.jobs as BroadcastJob[],
    total: parseInt(response.headers['x-total-count'] || String(response.data.total || 0), 10),
  };
};

export const fetchJob = async (id: string) => {
  const response = await axios.get(`${base()}/jobs/${id}`, {
    headers: authHeaders(),
  });
  return response.data.job as BroadcastJob;
};

export const fetchRecipients = async (jobId: string, page = 0, perPage = 50, status?: string) => {
  const response = await axios.get(`${base()}/jobs/${jobId}/recipients`, {
    headers: authHeaders(),
    params: {
      _start: page * perPage,
      _end: (page + 1) * perPage,
      status: status || undefined,
    },
  });
  return {
    recipients: response.data.recipients as BroadcastRecipient[],
    total: parseInt(response.headers['x-total-count'] || String(response.data.total || 0), 10),
  };
};

export const cancelJob = async (jobId: string) => {
  const response = await axios.post(`${base()}/jobs/${jobId}/cancel`, {}, {
    headers: authHeaders(),
  });
  return response.data.job as BroadcastJob;
};
